/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxfrag */

import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { LinkButton } from '@atlaskit/button/new';
import EmptyState from '@atlaskit/empty-state';
import { css, jsx } from '@compiled/react';
import React, { useMemo } from 'react';
import { injectIntl, type WrappedComponentProps } from 'react-intl-next';
import messages from '../messages';
import { getCrossJoinConfluenceEditorUrl } from '../utils';
import type OriginTracing from '@atlassiansox/origin-tracing';
import InlineJoinButton from '../inline-join-button/connected';
import ViewTracker from '../../../common/analytics/view-tracker';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/analytics-bridge';
import { AccessRequestCapabilityType } from '../../../state/confluence/access-request-capabilities/types';
import { token } from '@atlaskit/tokens';
import { N300 } from '@atlaskit/theme/colors';

interface OwnProps {
	onClick: (e: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => void;
	origin: OriginTracing | null;
	renderHeroImage: () => React.ReactNode;
	spaceKey: string | null;
	parentPageId: string | null;
	crossJoinProjectExperimentCohort?: string;
	accessRequestCapability?: AccessRequestCapabilityType;
}

const subtitleStyle = css({
	color: token('color.text.subtlest', N300),
	textAlign: 'center',
	marginTop: token('space.negative.300', '-24px'),
});

const RequestAccess = injectIntl(
	({
		intl: { formatMessage },
		onClick,
		origin,
		renderHeroImage,
		spaceKey,
		parentPageId,
		crossJoinProjectExperimentCohort = 'not-enrolled',
		accessRequestCapability,
	}: OwnProps & WrappedComponentProps) => {
		const requestAccessUrl = useMemo(
			() => getCrossJoinConfluenceEditorUrl(origin, spaceKey, parentPageId),
			[origin, spaceKey, parentPageId],
		);

		const inCrossJoinVariation = crossJoinProjectExperimentCohort === 'variation';

		const getSubtitle = () => {
			if (!(crossJoinProjectExperimentCohort === 'variation') || !accessRequestCapability) {
				return null;
			}
			if (accessRequestCapability === AccessRequestCapabilityType.REQUEST_ACCESS) {
				// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
				return <p css={subtitleStyle}>{formatMessage(messages.requestAccessButtonSubtitleExp)}</p>;
			}
			if (accessRequestCapability === AccessRequestCapabilityType.DIRECT_ACCESS) {
				// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
				return <p css={subtitleStyle}>{formatMessage(messages.joinProductButtonDescriptionExp)}</p>;
			}
		};

		const getPrimaryAction = () => {
			if (!(crossJoinProjectExperimentCohort === 'variation') || !accessRequestCapability) {
				return (
					<LinkButton
						appearance="primary"
						href={requestAccessUrl}
						target="_blank"
						onClick={onClick}
					>
						{formatMessage(messages.requestAccessButton)}
					</LinkButton>
				);
			}
			// Note that InlineJoinButton has ability to do RA as well
			// Inline join button will fire RA or DA API call to identity and refreshes accessRequestCapability state
			// Successfull RA / DA will go to PendingRequest screen
			if (
				accessRequestCapability === AccessRequestCapabilityType.REQUEST_ACCESS ||
				accessRequestCapability === AccessRequestCapabilityType.DIRECT_ACCESS
			) {
				return (
					<InlineJoinButton onClick={onClick}>
						{formatMessage(messages.requestAccessButtonExp)}
					</InlineJoinButton>
				);
			}
		};

		return (
			<>
				<EmptyState
					renderImage={renderHeroImage}
					header={formatMessage(
						inCrossJoinVariation ? messages.requestAccessTitleExp : messages.requestAccessTitle,
					)}
					description={formatMessage(
						inCrossJoinVariation
							? messages.requestAccessDescriptionExp
							: messages.requestAccessWithoutBetterTogetherDescription,
					)}
					primaryAction={getPrimaryAction()}
					testId="confluence-project-pages.cross-join.request-access"
				/>
				{getSubtitle()}
				<ViewTracker />
			</>
		);
	},
);

export default (props: OwnProps) => (
	<ContextualAnalyticsData sourceName="projectPagesRequestAccess" sourceType={SCREEN}>
		<RequestAccess {...props} />
	</ContextualAnalyticsData>
);
